import { useEffect, useRef, useState } from 'react'

// Shared per-table column controls: show/hide (checkbox menu) + drag-to-resize.
// Preferences persist per tableId in localStorage. Tables set table-layout:fixed
// with a <colgroup> so the widths here are authoritative and the table can
// scroll horizontally when the total exceeds the container.

export interface ColumnDef {
  key: string
  label: string
  width: number // default width px
  min?: number // min width px (default 60)
  fixed?: boolean // control column: always shown, hidden from the menu, no resizer
}

interface Stored {
  hidden?: string[]
  widths?: Record<string, number>
  wrap?: boolean
}
function read(id: string): Stored {
  try {
    return JSON.parse(localStorage.getItem('cols:' + id) || '{}')
  } catch {
    return {}
  }
}

export function useColumns(tableId: string, defs: ColumnDef[]) {
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(read(tableId).hidden ?? []))
  const [widths, setWidths] = useState<Record<string, number>>(() => read(tableId).widths ?? {})
  const [wrap, setWrap] = useState<boolean>(() => !!read(tableId).wrap)

  useEffect(() => {
    try {
      localStorage.setItem('cols:' + tableId, JSON.stringify({ hidden: [...hidden], widths, wrap }))
    } catch {
      /* ignore quota/availability */
    }
  }, [tableId, hidden, widths, wrap])

  const isVisible = (k: string) => !hidden.has(k)
  const toggle = (k: string) =>
    setHidden((h) => {
      const n = new Set(h)
      n.has(k) ? n.delete(k) : n.add(k)
      return n
    })
  const widthOf = (k: string) => widths[k] ?? defs.find((d) => d.key === k)?.width ?? 120
  const setWidth = (k: string, w: number) => {
    const min = defs.find((d) => d.key === k)?.min ?? 60
    setWidths((p) => ({ ...p, [k]: Math.max(min, Math.round(w)) }))
  }
  const visible = defs.filter((d) => isVisible(d.key))
  const totalWidth = visible.reduce((s, d) => s + widthOf(d.key), 0)
  return { defs, visible, isVisible, toggle, widthOf, setWidth, totalWidth, wrap, toggleWrap: () => setWrap((w) => !w) }
}

export type ColumnsApi = ReturnType<typeof useColumns>

function autoGrow(el: HTMLTextAreaElement) {
  el.style.height = 'auto'
  el.style.height = el.scrollHeight + 'px'
}

// A grid text cell that is a single-line <input> normally, or an auto-growing
// wrapping <textarea> when the table's "Wrap text" option is on. Enter/arrow
// keys are handled by the caller's onKeyDown (grid nav), so it behaves like the
// other cells regardless of mode.
export function WrapText({
  wrap,
  value,
  onChange,
  onKeyDown,
  disabled,
  placeholder,
  className,
  registerRef,
}: {
  wrap: boolean
  value: string
  onChange: (v: string) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  disabled?: boolean
  placeholder?: string
  className?: string
  registerRef: (el: HTMLInputElement | HTMLTextAreaElement | null) => void
}) {
  const ref = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
  const set = (el: HTMLInputElement | HTMLTextAreaElement | null) => {
    ref.current = el
    registerRef(el)
  }
  useEffect(() => {
    if (wrap && ref.current instanceof HTMLTextAreaElement) autoGrow(ref.current)
  }, [value, wrap])
  const cls = 'grid-cell w-full bg-transparent px-2 py-1 outline-none disabled:text-slate-400 ' + (className ?? '')
  if (wrap) {
    return (
      <textarea
        ref={set}
        rows={1}
        className={cls + ' resize-none overflow-hidden align-middle'}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
      />
    )
  }
  return (
    <input ref={set} className={cls} value={value} disabled={disabled} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} onKeyDown={onKeyDown} />
  )
}

// A <colgroup> matching the visible columns — gives the fixed layout its widths.
export function ColGroup({ cols }: { cols: ColumnsApi }) {
  return (
    <colgroup>
      {cols.visible.map((d) => (
        <col key={d.key} style={{ width: cols.widthOf(d.key) }} />
      ))}
    </colgroup>
  )
}

// Drag handle placed at the right edge of a (relative) <th>.
export function ColResizer({ cols, colKey }: { cols: ColumnsApi; colKey: string }) {
  return (
    <span
      onMouseDown={(e) => {
        e.preventDefault()
        e.stopPropagation()
        const startX = e.clientX
        const startW = cols.widthOf(colKey)
        const move = (ev: MouseEvent) => cols.setWidth(colKey, startW + (ev.clientX - startX))
        const up = () => {
          window.removeEventListener('mousemove', move)
          window.removeEventListener('mouseup', up)
        }
        window.addEventListener('mousemove', move)
        window.addEventListener('mouseup', up)
      }}
      className="absolute right-0 top-0 z-10 h-full w-1.5 cursor-col-resize select-none hover:bg-slate-300"
      aria-hidden
    />
  )
}

// Columns icon button + checkbox popover, for a table's top-right corner.
export function ColumnsMenu({ cols }: { cols: ColumnsApi }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('mousedown', h)
    return () => window.removeEventListener('mousedown', h)
  }, [open])

  return (
    <div className="relative" ref={ref}>
      <button
        className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
        title="Show / hide columns"
        aria-label="Show or hide columns"
        onClick={() => setOpen((v) => !v)}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="1.5" y="2.5" width="13" height="11" rx="1" />
          <line x1="6" y1="2.5" x2="6" y2="13.5" />
          <line x1="10.5" y1="2.5" x2="10.5" y2="13.5" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full z-30 mt-1 w-52 rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg">
          <label className="flex cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-slate-50">
            <input type="checkbox" checked={cols.wrap} onChange={cols.toggleWrap} />
            <span className="font-medium text-slate-700">Wrap text</span>
          </label>
          <div className="my-1 border-t border-slate-100" />
          <div className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Columns</div>
          {cols.defs
            .filter((d) => !d.fixed)
            .map((d) => (
              <label key={d.key} className="flex cursor-pointer items-center gap-2 px-3 py-1 hover:bg-slate-50">
                <input type="checkbox" checked={cols.isVisible(d.key)} onChange={() => cols.toggle(d.key)} />
                <span className="text-slate-700">{d.label}</span>
              </label>
            ))}
        </div>
      )}
    </div>
  )
}
